/**
 * DialogFooter — the one footer for every dialog (ORCH-424).
 *
 * Purpose: Decides where a dialog's buttons go and how much room the footer has, so no
 *          dialog re-specifies either. Cancel and the primary action sit together at the
 *          right, Cancel left of the primary. A destructive action (Delete, Remove,
 *          Deactivate) sits alone at the far left, away from that pair, so it is not hit
 *          by mistake. Dialogs with no destructive action look exactly as before.
 * Logic: `destructiveAction` renders first and takes the spare width as its right
 *        margin; `children` render after it in the order given. `sx` is deliberately not
 *        accepted: the footer's padding is set here, once.
 * Dependencies: MUI DialogActions, Box
 */
import React from 'react';
import { Box, DialogActions, DialogActionsProps } from '@mui/material';

export interface DialogFooterProps extends Omit<DialogActionsProps, 'sx'> {
  /**
   * Delete / Remove / Deactivate, as a `DestructiveButton`. Rendered alone at the far left.
   * Omit it (or pass `false` / `null`) when the dialog has no destructive action.
   */
  destructiveAction?: React.ReactNode;
  /** Cancel (or Close) followed by the primary action, in that order. */
  children?: React.ReactNode;
}

const DialogFooter: React.FC<DialogFooterProps> = ({ destructiveAction, children, ...rest }) => (
  <DialogActions sx={{ px: 3, py: 2 }} {...rest}>
    {destructiveAction ? (
      <Box sx={{ mr: 'auto' }} data-testid="dialog-footer-destructive">
        {destructiveAction}
      </Box>
    ) : null}
    {children}
  </DialogActions>
);

export default DialogFooter;
